import { findChild, nodeToText, filterChildren } from "@corca/core";
import { isEqual } from "es-toolkit";
import { createAtom } from "mobx";

import type { FlattenedNodeJson, LabelNodePosition, NodeJson } from "@corca/data";

// "& {}" prevents type flattening
type NodeId = string & {};
type BaseKey = string & {};
type FullKey = string & {};
type LabelText = string & {};

type NodeKeyParts = [
  type: NodeJson["type"],
  decorator: LabelText | null,
  labels: LabelText | null,
  paramCount: number,
];

export type GroupKeyResult = {
  base: BaseKey;
  type: NodeJson["type"];
  ids: Set<NodeId>;
};

type WithId<T extends object> = T & { id: NodeId };

export type GroupKeyFilter = (base: BaseKey, type: NodeJson["type"], ids: Set<NodeId>) => boolean;

// Uniqueness levels form a cumulative hierarchy
export const UNIQ_LEVELS = ["symbol", "type", "decorators", "labels", "params", "all"] as const;
export type UniquenessLevel = (typeof UNIQ_LEVELS)[number];

export interface NodeLookupOptions {
  /**
   * Uniqueness level for matching/grouping nodes. Forms a cumulative hierarchy where each level
   * includes all previous criteria:
   *
   * - "symbol": Only the symbol text (e.g., "x" === "x" regardless of type, decorators, or labels)
   * - "type": Symbol + node type (e.g., variable "x" !== constant "x")
   * - "decorators": Symbol + type + decorators (e.g., "x̂" !== "x")
   * ...
   * - "all": full uniqueness
   *
   * Default varies by method: has() default to "symbol", groupByKey() and buildKey() defaults to "all"
   */
  uniqBy?: UniquenessLevel;

  /** Filter by specific node types */
  types?: NodeJson["type"][];

  /** Filter function to include/exclude nodes from grouping */
  filter?: GroupKeyFilter;
}

/**
 * Optimized cache for storing and querying nodes based on their "key" properties (symbol, labels, params).
 */
export class NodeLookupModel {
  private atom = createAtom("NodeLookupModel");

  /* data maps */
  private nodeMap = new Map<NodeId, FlattenedNodeJson>();

  private nodeToParentMap = new Map<NodeId, NodeId>();

  /* key maps */
  private keyMap = new Map<FullKey, [Set<NodeId>, NodeKeyParts]>();

  private baseToFullKeysMap = new Map<BaseKey, Set<FullKey>>();

  constructor(
    nodesJson: FlattenedNodeJson[] | undefined,
    private keySeparator = "\x00",
  ) {
    this.initializeCache(nodesJson);
  }

  updateNodeWithAncestors(nodeId: NodeId, node: FlattenedNodeJson) {
    const changed = this.updateNode(nodeId, node);
    if (!changed) return;

    this.atom.reportChanged();

    let currentId: NodeId | undefined = nodeId;
    while (currentId) {
      const currentNode = this.nodeMap.get(currentId);
      if (currentNode && this.isIdentityBearingNode(currentNode)) {
        this.deleteFullKey(currentNode.id);
        this.updateKeyMap(currentNode);
      }
      currentId = this.nodeToParentMap.get(currentId);
    }
  }

  /** Dispose of the cache and unregister observers to prevent memory leaks. */
  dispose() {
    // Unregister the observer
    // this.yNodeMap.unobserveDeep(this.observeDeepHandler);

    // Clear all maps
    this.keyMap.clear();
    this.baseToFullKeysMap.clear();
    this.nodeMap.clear();
    this.nodeToParentMap.clear();
  }

  /** Returns iterator of all full key entries. */
  get entries() {
    this.atom.reportObserved();

    return this.keyMap.entries();
  }

  /** Returns iterator of all base keys in the cache. */
  get keys() {
    this.atom.reportObserved();

    return this.baseToFullKeysMap.keys();
  }

  /** Builds a cache key from node properties based on specified uniqueness level. */
  buildKey(node: FlattenedNodeJson | NodeJson, options?: NodeLookupOptions): FullKey | null {
    const uniqBy = options?.uniqBy ?? "all";
    const [base, keys] = this.getNodeKeyParts(node, uniqBy);
    const level = UNIQ_LEVELS.indexOf(uniqBy);

    // Build key with only the parts relevant to the uniqueness level
    const parts: (string | number)[] = [base];
    for (let i = 0; i < keys.length; i++) {
      if (i < level) {
        parts.push(keys[i] ?? (UNIQ_LEVELS[i + 1] === "params" ? 0 : ""));
      } else {
        parts.push(UNIQ_LEVELS[i + 1] === "params" ? 0 : "");
      }
    }

    return parts.join(this.keySeparator);
  }

  /**
   * Checks if a node or symbol exists in the cache, optionally filtered by type and uniqueness level.
   *
   * Note: this method is NOT observable and will not trigger reaction on changes.
   *
   * @example
   * // Check if any constant with symbol "x" exists, regardless of labels or decorators
   * cache.has("x", { types: ["constant"] });
   *
   * @example
   * // Check if a function with symbol "f" and same amount of parameters exists
   * const node = {
   *   type: "function",
   *   props: {...},
   *   children: [
   *     { type: "symbol", props: { value: "f" }, children: [] },
   *     { type: "inline", props: {}, children: [] },
   *     { type: "inline", props: {}, children: [] },
   *   ]
   * };
   * cache.has(node, { types: ["function"], uniqBy: "all" });
   */
  has(nodeOrSymbol: NodeJson | FlattenedNodeJson | string, options?: NodeLookupOptions): boolean {
    const uniqBy = options?.uniqBy ?? "symbol";
    const [base, keys]: [BaseKey, NodeKeyParts] =
      typeof nodeOrSymbol === "string"
        ? [nodeOrSymbol, ["variable", null, null, 0]]
        : this.getNodeKeyParts(nodeOrSymbol, uniqBy);
    if (!base) return false;

    const fullKeys = this.baseToFullKeysMap.get(base);
    if (!fullKeys || fullKeys.size === 0) return false;

    const types = options?.types;

    // If checking by symbol only and no type filter, just check if fullKeys has entries
    if (uniqBy === "symbol" && !types) {
      return fullKeys.size > 0;
    }

    const level = UNIQ_LEVELS.indexOf(uniqBy);

    for (const fullKey of fullKeys) {
      const entry = this.keyMap.get(fullKey);
      if (!entry) continue;

      const [, keyParts] = entry;

      // Check type filter
      if (types && !types.includes(keyParts[0])) {
        continue;
      }

      // Compare relevant key parts based on uniqueness level
      let matches = true;
      for (let i = 0; i < level; i++) {
        if (keyParts[i] !== keys[i]) {
          matches = false;
          break;
        }
      }

      if (matches) return true;
    }

    return false;
  }

  /**
   * Groups nodes by their cache keys, optionally filtered, returning base key, type, and node IDs.
   *
   * @example
   * // Group all nodes by symbol and type, ignoring labels and decorators
   * const groups = cache.groupByKey({ uniqBy: "type" });
   *
   * @example
   * // Group "x" variables with different labels together
   * const groups = cache.groupByKey({
   *   filter: (base, type) => base === "x" && type === "variable",
   *   uniqBy: "labels"
   * });
   */
  groupByKey(options?: NodeLookupOptions): Map<FullKey, GroupKeyResult> {
    this.atom.reportObserved();

    const result = new Map<FullKey, GroupKeyResult>();
    const uniqBy = options?.uniqBy ?? "all";
    const filter = options?.filter;
    const level = UNIQ_LEVELS.indexOf(uniqBy);

    for (const [base, fullKeys] of this.baseToFullKeysMap.entries()) {
      for (const fullKey of fullKeys) {
        const entry = this.keyMap.get(fullKey);
        if (!entry) continue;

        const [ids, keyParts] = entry;
        const [keyType] = keyParts;

        if (filter && !filter(base, keyType, ids)) {
          continue;
        }

        // Build grouped key based on uniqueness level
        const groupedKeyParts: (string | number)[] = [base];

        for (let i = 0; i < keyParts.length; i++) {
          if (i < level) {
            groupedKeyParts.push(keyParts[i] ?? (UNIQ_LEVELS[i + 1] === "params" ? 0 : ""));
          } else {
            groupedKeyParts.push(UNIQ_LEVELS[i + 1] === "params" ? 0 : "");
          }
        }

        const groupedKey = groupedKeyParts.join(this.keySeparator);

        const existing = result.get(groupedKey);
        if (!existing) {
          result.set(groupedKey, { base, ids: new Set(ids), type: keyType });
        } else {
          existing.ids = new Set([...existing.ids, ...ids]);
        }
      }
    }

    return result;
  }

  private initializeCache(nodesJson?: FlattenedNodeJson[]) {
    const keyedNodes: WithId<FlattenedNodeJson>[] = [];
    if (nodesJson) {
      for (const node of nodesJson) {
        if (this.withId(node)) {
          this.updateNode(node.id, node);
          if (this.isIdentityBearingNode(node)) {
            keyedNodes.push(node);
          }
        }
      }
    }

    for (const node of keyedNodes) {
      this.updateKeyMap(node);
    }
  }

  private withId<T extends object>(node: T | undefined): node is WithId<T> {
    return node ? "id" in node : false;
  }

  private isIdentityBearingNode(node: FlattenedNodeJson): node is WithId<FlattenedNodeJson> {
    return (
      "id" in node &&
      (node.type === "variable" || node.type === "constant" || node.type === "function")
    );
  }

  private updateNode(nodeId: NodeId, node: FlattenedNodeJson): boolean {
    let changed = false;

    if (!this.nodeMap.has(nodeId) || !isEqual(this.nodeMap.get(nodeId), node)) {
      changed = true;
      this.nodeMap.set(nodeId, node);
    }

    for (const childId of node.childrenIds) {
      if (!this.nodeToParentMap.has(childId) || this.nodeToParentMap.get(childId) !== nodeId) {
        changed = true;
        this.nodeToParentMap.set(childId, nodeId);
      }
    }

    return changed;
  }

  deleteNode(id: NodeId) {
    if (!this.nodeMap.has(id)) return;

    this.atom.reportChanged();

    const node = this.nodeMap.get(id);
    this.deleteFullKey(id);
    this.nodeMap.delete(id);
    this.nodeToParentMap.delete(id);

    if (node) {
      for (const childId of node.childrenIds) {
        if (this.nodeToParentMap.get(childId) === id) {
          this.nodeToParentMap.delete(childId);
        }
      }
    }
  }

  private deleteFullKey(id: NodeId) {
    // Search through keyMap to find the entry containing this node ID
    for (const [fullKey, [ids]] of this.keyMap.entries()) {
      if (ids.has(id)) {
        ids.delete(id);

        if (ids.size === 0) {
          this.keyMap.delete(fullKey);

          for (const [baseKey, fullKeys] of this.baseToFullKeysMap.entries()) {
            if (fullKeys.has(fullKey)) {
              fullKeys.delete(fullKey);
              if (fullKeys.size === 0) {
                this.baseToFullKeysMap.delete(baseKey);
              }
              break;
            }
          }
        }

        break;
      }
    }
  }

  private updateKeyMap(node: WithId<FlattenedNodeJson>) {
    const [base, keys] = this.getNodeKeyParts(node);
    if (!base) return;

    const fullKey = [base, ...keys].join(this.keySeparator);

    const entry = this.keyMap.get(fullKey);
    if (!entry) {
      this.keyMap.set(fullKey, [new Set([node.id]), keys]);
    } else {
      entry[0].add(node.id);
    }

    // Update baseToFullKeysMap
    let fullKeys = this.baseToFullKeysMap.get(base);
    if (!fullKeys) {
      fullKeys = new Set();
      this.baseToFullKeysMap.set(base, fullKeys);
    }
    fullKeys.add(fullKey);
  }

  private getLabelText(
    node: FlattenedNodeJson | NodeJson | null,
    position: LabelNodePosition,
  ): LabelText | null {
    if (!node) return null;

    let label = "";

    if ("children" in node) {
      const labels = node.children.filter((child) => {
        return child.type === "label" && child.props.position === position;
      });

      if (labels.length === 0) return null;

      for (const l of labels) {
        const inline = findChild(l, "inline", this.nodeMap);
        if (inline) label += nodeToText(inline);
      }
    } else {
      const labelIds = node.childrenIds.filter((id) => {
        const child = this.nodeMap.get(id);
        return child?.type === "label" && child.props.position === position;
      });

      if (labelIds.length === 0) return null;

      for (const id of labelIds) {
        const child = this.nodeMap.get(id);
        if (child) {
          const inline = findChild(child, "inline", this.nodeMap);
          if (inline) label += nodeToText(inline, this.nodeMap);
        }
      }
    }

    return label || null;
  }

  private getDecoratorText(node: FlattenedNodeJson | NodeJson | null): LabelText | null {
    if (!node) return null;

    const decorators: string[] = [];

    if ("children" in node) {
      const decs = node.children.filter(
        (child): child is Extract<NodeJson, { type: "decorator" }> => child.type === "decorator",
      );

      for (const d of decs) {
        decorators.push(d.props.name);
      }
    } else {
      const decIds = node.childrenIds.filter((id) => {
        const child = this.nodeMap.get(id);
        return child?.type === "decorator";
      });

      for (const id of decIds) {
        const child = this.nodeMap.get(id) as
          | Extract<FlattenedNodeJson, { type: "decorator" }>
          | undefined;
        if (child) {
          decorators.push(child.props.name);
        }
      }
    }

    return decorators.length ? decorators.join(";") : null;
  }

  private getAllLabelsText(node: FlattenedNodeJson | NodeJson | null): LabelText | null {
    if (!node) return null;

    const positions: Array<[LabelNodePosition, string]> = [
      ["right-top", "rt"],
      ["right-bottom", "rb"],
      ["left-top", "lt"],
      ["left-bottom", "lb"],
    ];

    const parts: string[] = [];
    for (const [position, prefix] of positions) {
      const text = this.getLabelText(node, position);
      if (text) {
        parts.push(`${prefix}(${text})`);
      }
    }

    return parts.length > 0 ? parts.join(";") : null;
  }

  private getNodeKeyParts(
    node: FlattenedNodeJson | NodeJson,
    uniqBy: UniquenessLevel = "all",
  ): [BaseKey, NodeKeyParts] {
    const symbol = findChild(node, "symbol", this.nodeMap);
    const base = symbol?.props.value || "";
    const { type } = node;

    const level = UNIQ_LEVELS.indexOf(uniqBy);

    // Cumulative hierarchy - each level includes all previous levels
    // Note: type is always stored but only compared when level >= 1 (uniqBy: "type" or higher)
    const decorator =
      symbol && level >= UNIQ_LEVELS.indexOf("decorators") ? this.getDecoratorText(symbol) : null;
    const labels =
      symbol && level >= UNIQ_LEVELS.indexOf("labels") ? this.getAllLabelsText(symbol) : null;
    const params =
      level >= UNIQ_LEVELS.indexOf("params")
        ? filterChildren(node, (child) => child.type === "inline", this.nodeMap).length
        : 0;

    return [base, [type, decorator, labels, params]];
  }
}
